import { useEffect, useState } from "react";
import { useRadio, computePosition, getPool } from "@/context/RadioContext";
import { Reveal } from "@/components/Reveal";
import { Play, Pause, Loader2, Radio as RadioIcon, Music } from "lucide-react";

const fmtDur = (s) => { const m = Math.floor((s || 0) / 60); const sec = Math.round((s || 0) % 60); return `${m}:${String(sec).padStart(2, "0")}`; };

export default function OnAir() {
  const { config, available, playing, loading, nowTitle, upNext, toggle } = useRadio();
  const [liveIdx, setLiveIdx] = useState(-1);

  useEffect(() => {
    if (!config || config.mode === "external") return;
    const tick = () => { const p = computePosition(config); setLiveIdx(p ? p.idx : -1); };
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [config]);

  const external = config?.mode === "external";
  const tracks = getPool(config);

  return (
    <div className="min-h-screen bg-navy text-bone" data-testid="onair-page">
      <div className="aurora-layer absolute inset-0 pointer-events-none opacity-30 overflow-hidden" aria-hidden="true">
        <div className="aurora aurora-1" />
        <div className="aurora aurora-2" />
      </div>
      <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 pt-40 pb-28">
        <Reveal>
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-gold">
            <span className={`w-2 h-2 rounded-full ${playing ? "bg-rust animate-pulse" : "bg-bone/40"}`} />
            {config?.station_name || "IHIMM Radio"}
          </span>
          <h1 className="heading-light font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-5">On the Air.</h1>
          <p className="font-sans text-bone/70 leading-relaxed mt-5 max-w-xl">
            Worship, the Word, and encouragement for the brotherhood — streaming while you browse. Press play and keep it on in the background.
          </p>
          {config?.schedule_enabled && config.active_block && (
            <p className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold/90 mt-5 border border-gold/30 px-3 py-1.5" data-testid="onair-block">
              Current program — {config.active_block.label}
            </p>
          )}
        </Reveal>

        {!available ? (
          <Reveal delay={0.1}>
            <div className="mt-14 border border-white/15 p-10 max-w-lg" data-testid="onair-offline">
              <RadioIcon size={22} className="text-gold" strokeWidth={1.5} />
              <p className="font-serif italic text-xl mt-4 text-bone/85">The station is quiet right now.</p>
              <p className="font-sans text-sm text-bone/55 mt-2">Check back soon — new broadcasts are on the way.</p>
            </div>
          </Reveal>
        ) : (
          <>
            <Reveal delay={0.1}>
              <div className="mt-14 flex items-center gap-6 flex-wrap" data-testid="onair-player">
                <button onClick={toggle} data-testid="onair-play-btn" aria-label={playing ? "Pause" : "Listen live"}
                  className="w-20 h-20 rounded-full bg-gold text-navy flex items-center justify-center hover:scale-105 transition-transform shadow-[0_0_60px_rgba(212,175,55,0.35)]">
                  {loading ? <Loader2 size={28} className="animate-spin" /> : playing ? <Pause size={28} /> : <Play size={28} className="ml-1" />}
                </button>
                <div className="min-w-0">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/50">{playing ? "Now playing" : "Tap to listen live"}</span>
                  <p className="font-serif text-2xl sm:text-3xl mt-1 truncate text-bone" data-testid="onair-now-playing">
                    {playing && nowTitle ? nowTitle : external ? (config.external_label || "Live Stream") : "Join the broadcast"}
                  </p>
                  {playing && upNext && <p className="font-sans text-sm text-bone/55 mt-1" data-testid="onair-up-next">Up next: {upNext}</p>}
                </div>
              </div>
              <p className="font-sans text-xs text-bone/40 mt-6">Sound requires one tap — your device blocks auto-playing audio until you press play.</p>
            </Reveal>

            {!external && tracks.length > 0 && (
              <Reveal delay={0.15}>
                <div className="mt-20" data-testid="onair-lineup">
                  <h2 className="heading-light font-mono text-xs uppercase tracking-[0.3em] opacity-60 mb-6">Program Lineup</h2>
                  <div className="divide-y divide-white/10 border-y border-white/10">
                    {tracks.map((t, i) => (
                      <div key={t.id} data-testid={`lineup-${t.id}`} className={`flex items-center gap-4 py-4 ${i === liveIdx ? "text-gold" : "text-bone/75"}`}>
                        <span className="font-mono text-xs w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                        <Music size={15} strokeWidth={1.5} className="shrink-0" />
                        <span className="font-sans text-sm sm:text-base flex-1 min-w-0 truncate">{t.title}</span>
                        {i === liveIdx && <span className="font-mono text-[9px] uppercase tracking-[0.25em] flex items-center gap-1.5 shrink-0"><span className="w-1.5 h-1.5 rounded-full bg-rust animate-pulse" /> Live now</span>}
                        <span className="font-mono text-xs text-bone/40 shrink-0">{fmtDur(t.duration)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="font-sans text-xs text-bone/40 mt-4">The station plays continuously — every listener hears the same moment, just like live radio.</p>
                </div>
              </Reveal>
            )}
          </>
        )}
      </div>
    </div>
  );
}
